import type { ContactNumber } from '@hue/contracts';
import { ContactNumbersEditor } from './contact-numbers-editor.js';
import { contactNumbersError, copiedContactNumbers } from './company-contact-model.js';
import { BRAZILIAN_STATES, countryCodes, formatPhone, isValidCnpj } from '@hue/contracts';
import {
  Button,
  Combobox,
  DocumentInput,
  FormErrorSummary,
  Input,
  MaskedInput,
  Modal,
  Select,
  Switch,
  formatBrazilianDocument,
  useSnackbar,
} from '@hue/design-system';
import { useQueryClient } from '@tanstack/react-query';
import { useId, useMemo, useState } from 'react';
import {
  businessUnitApi,
  companyIdentityApi,
  getApiError,
  getApiErrorField,
  type BrandLogoSet,
  type BusinessUnit,
  type BusinessUnitManagement,
} from '../lib/api.js';
import { refreshSession } from '../lib/session.js';
import { brandQueryKey, EMPTY_LOGOS } from './brand.js';
import { BrandLogoSlot } from './brand-logo-slot.js';
import { logoSlots } from './brand-logo-slots.js';
import {
  openingHourDrafts,
  openingHoursError,
  openingHoursOf,
  type OpeningHourDraft,
} from './business-unit-opening-hours-model.js';
import { BusinessUnitOpeningHours } from './business-unit-opening-hours.js';
import { CancellationPolicySection } from '../modules/scheduling/index.js';
import type { SocialLink } from '@hue/contracts';
import { SocialLinksEditor } from './social-links-editor.js';
import { socialLinksError } from './social-links-model.js';
import { TimeZoneSelect } from './time-zone-select.js';

const countryNames = new Intl.DisplayNames(['pt-BR'], { type: 'region' });

type PostalCodeState = 'idle' | 'loading' | 'found' | 'missing' | 'down';

interface UnitForm {
  contactNumbers: ContactNumber[];
  socialLinks: SocialLink[];
  allowClientProfessionalChoice: boolean;
  openingHours: OpeningHourDraft[];
  displayName: string;
  timeZone: string;
  phone: string;
  email: string;
  taxId: string;
  country: string;
  postalCode: string;
  street: string;
  number: string;
  complement: string;
  district: string;
  city: string;
  state: string;
}

type FieldErrors = Partial<Record<keyof UnitForm, string | undefined>>;

const kindLabels = { ESTABLISHMENT: 'Estabelecimento', SUPPORT: 'Apoio' } as const;

function formOf(unit: BusinessUnit): UnitForm {
  const brazil = (unit.address?.country ?? 'BR') === 'BR';
  const postalCode = unit.address?.postalCode ?? '';
  return {
    displayName: unit.displayName,
    contactNumbers:
      unit.contactNumbers ??
      (unit.phone
        ? [{ label: 'Atendimento', number: unit.phone, channels: ['CALL'], isPublic: true }]
        : []),
    socialLinks: unit.socialLinks ?? [],
    allowClientProfessionalChoice: unit.allowClientProfessionalChoice ?? false,
    openingHours: openingHourDrafts(unit.openingHours ?? []),
    timeZone: unit.timeZone,
    phone: formatPhone(unit.phone) ?? '',
    email: unit.email ?? '',
    taxId: unit.taxId ? formatBrazilianDocument(unit.taxId, 'CNPJ') : '',
    country: unit.address?.country ?? 'BR',
    postalCode:
      brazil && /^\d{8}$/u.test(postalCode)
        ? `${postalCode.slice(0, 5)}-${postalCode.slice(5)}`
        : postalCode,
    street: unit.address?.street ?? '',
    number: unit.address?.number ?? '',
    complement: unit.address?.complement ?? '',
    district: unit.address?.district ?? '',
    city: unit.address?.city ?? '',
    state: unit.address?.state ?? '',
  };
}

/// O que dá para conferir antes de mandar. O servidor repete tudo e é quem decide.
function validate(form: UnitForm): FieldErrors {
  const errors: FieldErrors = {};
  if (form.displayName.replace(/\s+/gu, ' ').trim().length < 2) {
    errors.displayName = 'Informe o nome da unidade com pelo menos 2 caracteres.';
  }
  const taxId = form.taxId.replace(/\D/gu, '');
  if (taxId && !isValidCnpj(taxId)) errors.taxId = 'Informe um CNPJ válido.';
  if (form.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/u.test(form.email.trim())) {
    errors.email = 'Informe um e-mail válido.';
  }
  const postalCode = form.postalCode.replace(/\D/gu, '');
  if (form.country === 'BR' && postalCode && postalCode.length !== 8) {
    errors.postalCode = 'O CEP tem 8 dígitos.';
  }
  errors.contactNumbers = contactNumbersError(form.contactNumbers);
  errors.socialLinks = socialLinksError(form.socialLinks);
  errors.openingHours = openingHoursError(form.openingHours);
  return errors;
}

/// Nome, contato, endereço e logos próprias da unidade (ADR 0040). Tipo e chave não mudam.
export function BusinessUnitDialog({
  unit,
  logos,
  companyLogos,
  onClose,
  onSaved,
}: {
  unit: BusinessUnit;
  logos: BrandLogoSet | undefined;
  companyLogos: BrandLogoSet;
  onClose: () => void;
  onSaved: (management: BusinessUnitManagement) => void;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const formId = useId();
  const [form, setForm] = useState<UnitForm>(() => formOf(unit));
  const [errors, setErrors] = useState<FieldErrors>({});
  const [requestError, setRequestError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [postalCodeState, setPostalCodeState] = useState<PostalCodeState>('idle');
  const brazil = form.country === 'BR';
  const own = logos ?? EMPTY_LOGOS;
  const countries = useMemo(
    () =>
      countryCodes()
        .map((code) => ({ value: code, label: countryNames.of(code) ?? code }))
        .sort((a, b) =>
          a.value === 'BR' ? -1 : b.value === 'BR' ? 1 : a.label.localeCompare(b.label, 'pt-BR'),
        ),
    [],
  );

  function set<K extends keyof UnitForm>(field: K, value: UnitForm[K]) {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  }

  async function lookupPostalCode(text: string) {
    const digits = text.replace(/\D/gu, '');
    if (digits.length !== 8) return;
    setPostalCodeState('loading');
    try {
      const address = await businessUnitApi.findPostalCode(digits);
      setForm((current) =>
        current.postalCode.replace(/\D/gu, '') !== digits
          ? current
          : {
              ...current,
              street: address.street ?? current.street,
              district: address.district ?? current.district,
              city: address.city,
              state: address.state,
            },
      );
      setPostalCodeState('found');
    } catch (cause) {
      const status = (cause as { response?: { status?: number } }).response?.status;
      setPostalCodeState(status === 404 ? 'missing' : 'down');
    }
  }

  async function copyCompanyContacts() {
    setCopying(true);
    setRequestError(undefined);
    try {
      const company = await companyIdentityApi.profile();
      const contacts = company.contactDetails;
      if (!contacts || (!contacts.contactNumbers.length && !contacts.email)) {
        setRequestError('Cadastre os contatos em Dados da empresa antes de copiar.');
        return;
      }
      if (copiedContactNumbers(form.contactNumbers, contacts.contactNumbers).length > 20) {
        setRequestError('A cópia ultrapassa o limite de 20 contatos.');
        return;
      }
      setForm((current) => ({
        ...current,
        contactNumbers: copiedContactNumbers(current.contactNumbers, contacts.contactNumbers),
        email: current.email || contacts.email || '',
      }));
      snackbar.success(
        'Contatos copiados para o formulário. Escolha os públicos e salve a unidade.',
      );
    } catch (cause) {
      setRequestError(getApiError(cause));
    } finally {
      setCopying(false);
    }
  }

  async function copyCompanyField(kind: 'address' | 'taxId') {
    setCopying(true);
    setRequestError(undefined);
    try {
      const details = (await companyIdentityApi.profile()).contactDetails;
      const source = details?.[kind];
      if (!source) {
        setRequestError(
          kind === 'address'
            ? 'Cadastre o endereço em Dados da empresa antes de copiar.'
            : 'Cadastre o CNPJ em Dados da empresa antes de copiar.',
        );
        return;
      }
      if (kind === 'taxId' && details?.taxId) {
        set('taxId', formatBrazilianDocument(details.taxId, 'CNPJ'));
      } else if (kind === 'address' && details?.address) {
        const address = details.address;
        const postal = address.postalCode ?? '';
        setForm((current) => ({
          ...current,
          country: address.country,
          postalCode:
            address.country === 'BR' ? postal.replace(/^(\d{5})(\d{3})$/u, '$1-$2') : postal,
          street: address.street ?? '',
          number: address.number ?? '',
          complement: address.complement ?? '',
          district: address.district ?? '',
          city: address.city ?? '',
          state: address.state ?? '',
        }));
        setErrors((current) => ({ ...current, postalCode: undefined, state: undefined }));
        setPostalCodeState('idle');
      }
      snackbar.success(
        kind === 'address'
          ? 'Endereço copiado para o formulário. Confira e salve a unidade.'
          : 'CNPJ copiado para o formulário. Confira e salve a unidade.',
      );
    } catch (cause) {
      setRequestError(getApiError(cause));
    } finally {
      setCopying(false);
    }
  }

  async function save() {
    setRequestError(undefined);
    const found = validate(form);
    setErrors(found);
    if (Object.values(found).some(Boolean)) return;
    const address = {
      country: form.country,
      postalCode: form.postalCode.trim() || null,
      street: form.street.trim() || null,
      number: form.number.trim() || null,
      complement: form.complement.trim() || null,
      district: form.district.trim() || null,
      city: form.city.trim() || null,
      state: form.state.trim() || null,
    };
    const hasAddress = Object.entries(address).some(([key, part]) => key !== 'country' && part);
    setSaving(true);
    try {
      const management = await businessUnitApi.update(unit.id, {
        displayName: form.displayName,
        contactNumbers: form.contactNumbers,
        socialLinks: form.socialLinks,
        allowClientProfessionalChoice: form.allowClientProfessionalChoice,
        ...(unit.kind === 'ESTABLISHMENT'
          ? { openingHours: openingHoursOf(form.openingHours) }
          : {}),
        timeZone: form.timeZone,
        phone: form.phone.trim() || null,
        email: form.email.trim() || null,
        taxId: form.taxId.trim() || null,
        address: hasAddress ? address : null,
      });
      onSaved(management);
      await refreshSession(queryClient);
      snackbar.success('Unidade salva.');
      onClose();
    } catch (cause) {
      const field = getApiErrorField(cause);
      const target = field as keyof UnitForm | undefined;
      if (target && target in form) setErrors({ [target]: getApiError(cause) });
      else setRequestError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }

  const postalCodeHint = {
    idle: brazil ? 'Com o CEP, o endereço vem preenchido.' : undefined,
    loading: 'Buscando o endereço…',
    found: 'Endereço preenchido pelo CEP. Confira e informe o número.',
    missing: 'CEP não encontrado. Preencha o endereço à mão.',
    down: 'Não foi possível consultar o CEP agora. Preencha à mão.',
  }[postalCodeState];

  async function refreshBrand() {
    await queryClient.invalidateQueries({ queryKey: brandQueryKey });
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={unit.displayName}
      description={`${kindLabels[unit.kind]} · identidade, contato e regras desta unidade.`}
      className="max-w-3xl"
      footer={
        <div className="flex flex-wrap items-center justify-end gap-3">
          <FormErrorSummary form={formId} className="basis-full sm:basis-auto sm:flex-1" />
          {requestError ? (
            <p role="alert" className="basis-full text-sm text-danger">
              {requestError}
            </p>
          ) : null}
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" form={formId} loading={saving} disabled={copying}>
            Salvar unidade
          </Button>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="grid gap-6"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_12rem]">
          <Input
            label="Nome da unidade"
            value={form.displayName}
            maxLength={160}
            error={errors.displayName}
            aria-invalid={errors.displayName ? true : undefined}
            onChange={(event) => set('displayName', event.target.value)}
          />
          <Input label="Tipo" value={kindLabels[unit.kind]} readOnly disabled />
        </div>

        {/* O dia civil da unidade (ADR 0044): agenda, estoque, compras e caixa dela contam o dia
          por este relógio. Mudar não reescreve o que já foi gravado. */}
        <div className="grid gap-2">
          <TimeZoneSelect
            label="Fuso horário da unidade"
            value={form.timeZone}
            onValueChange={(value) => set('timeZone', value)}
            error={errors.timeZone}
          />
          <p className="text-xs leading-5 text-content-muted">
            Agenda, estoque, compras e caixa desta unidade contam o dia por este horário. O que já
            foi registrado não muda.
          </p>
        </div>

        {unit.kind === 'ESTABLISHMENT' ? (
          <fieldset className="grid gap-3">
            <legend className="mb-1 text-sm font-semibold">Agendamento pelo cliente</legend>
            <Switch
              checked={form.allowClientProfessionalChoice}
              onChange={(checked) => set('allowClientProfessionalChoice', checked)}
            >
              Permitir que o cliente escolha o profissional
            </Switch>
            <p className="text-xs leading-5 text-content-muted">
              Vale para o agendamento pelo portal do cliente, que só mostra quem realiza o serviço
              escolhido. A sala é definida pela equipe da unidade. A agenda interna mantém as
              permissões da equipe.
            </p>
          </fieldset>
        ) : null}

        {unit.kind === 'ESTABLISHMENT' ? (
          <BusinessUnitOpeningHours
            value={form.openingHours}
            onChange={(value) => set('openingHours', value)}
            error={errors.openingHours}
          />
        ) : null}

        <fieldset className="grid gap-4">
          <legend className="mb-1 text-sm font-semibold">
            Contato{' '}
            <span className="font-normal text-content-muted">· opcional, sai no timbre</span>
          </legend>
          <div className="grid gap-4 sm:grid-cols-3">
            <Input
              label="E-mail"
              type="email"
              value={form.email}
              error={errors.email}
              aria-invalid={errors.email ? true : undefined}
              onChange={(event) => set('email', event.target.value)}
            />
            <div className="grid content-start gap-2">
              <DocumentInput
                kind="CNPJ"
                value={form.taxId}
                onValueChange={(text) => set('taxId', text)}
                error={errors.taxId}
              />
              <Button
                type="button"
                variant="outline"
                disabled={saving || copying}
                onClick={() => void copyCompanyField('taxId')}
              >
                Copiar CNPJ da empresa
              </Button>
            </div>
          </div>
        </fieldset>

        <div>
          <Button
            type="button"
            variant="outline"
            disabled={saving || copying}
            onClick={() => void copyCompanyContacts()}
          >
            Copiar contatos da empresa
          </Button>
        </div>
        <ContactNumbersEditor
          value={form.contactNumbers}
          onChange={(value) => set('contactNumbers', value)}
          disabled={saving || copying}
          error={errors.contactNumbers}
        />
        <SocialLinksEditor
          value={form.socialLinks}
          onChange={(value) => set('socialLinks', value)}
          disabled={saving}
          error={errors.socialLinks}
        />

        <fieldset className="grid gap-4">
          <legend className="mb-1 text-sm font-semibold">
            Endereço{' '}
            <span className="font-normal text-content-muted">
              · sem cidade, o documento deixa o espaço para escrever à mão
            </span>
          </legend>
          <div>
            <Button
              type="button"
              variant="outline"
              disabled={saving || copying}
              onClick={() => void copyCompanyField('address')}
            >
              Copiar endereço da empresa
            </Button>
            <p className="mt-2 text-xs text-content-muted">
              Substitui o endereço deste formulário. Confira antes de salvar.
            </p>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <Combobox
              label="País"
              placeholder="Buscar país"
              selectedLabel={countryNames.of(form.country) ?? form.country}
              options={countries}
              onSelect={(choice) => {
                const country = (choice as { value: string }).value;
                if (country === form.country) return;
                setForm((current) => ({ ...current, country, postalCode: '', state: '' }));
                setPostalCodeState('idle');
              }}
              onClear={() => set('country', 'BR')}
              emptyLabel="Nenhum país encontrado"
            />
            {brazil ? (
              <MaskedInput
                label="CEP"
                inputMode="numeric"
                placeholder="00000-000"
                value={form.postalCode}
                format={(digits) => digits.slice(0, 8).replace(/^(\d{5})(\d)/u, '$1-$2')}
                onValueChange={(text) => {
                  set('postalCode', text);
                  setPostalCodeState('idle');
                  void lookupPostalCode(text);
                }}
                error={errors.postalCode}
                hint={postalCodeHint}
                hintTone={
                  postalCodeState === 'found'
                    ? 'positive'
                    : postalCodeState === 'missing' || postalCodeState === 'down'
                      ? 'warning'
                      : 'muted'
                }
              />
            ) : (
              <Input
                label="Código postal"
                value={form.postalCode}
                error={errors.postalCode}
                onChange={(event) => set('postalCode', event.target.value)}
              />
            )}
          </div>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_8rem_minmax(0,12rem)]">
            <Input
              label="Rua"
              value={form.street}
              error={errors.street}
              onChange={(event) => set('street', event.target.value)}
            />
            <Input
              label="Número"
              value={form.number}
              error={errors.number}
              onChange={(event) => set('number', event.target.value)}
            />
            <Input
              label="Complemento"
              placeholder="Sala, andar…"
              value={form.complement}
              error={errors.complement}
              onChange={(event) => set('complement', event.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_9rem]">
            <Input
              label="Bairro"
              value={form.district}
              error={errors.district}
              onChange={(event) => set('district', event.target.value)}
            />
            <Input
              label="Cidade"
              value={form.city}
              error={errors.city}
              onChange={(event) => set('city', event.target.value)}
            />
            {brazil ? (
              <Select
                label="UF"
                value={form.state}
                options={[
                  { value: '', label: 'UF' },
                  ...BRAZILIAN_STATES.map((state) => ({ value: state, label: state })),
                ]}
                onValueChange={(value) => set('state', value)}
                error={errors.state}
              />
            ) : (
              <Input
                label="Estado ou província"
                value={form.state}
                onChange={(event) => set('state', event.target.value)}
              />
            )}
          </div>
        </fieldset>
      </form>

      <section
        aria-labelledby={`${formId}-logos`}
        className="mt-6 grid gap-4 border-t border-surface-border pt-6"
      >
        <div>
          <h3 id={`${formId}-logos`} className="text-sm font-semibold">
            Logos desta unidade
          </h3>
          <p className="mt-1 text-sm text-content-muted">
            Opcional. O formato sem arquivo aqui usa o da empresa. Prefira SVG.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-3">
          {logoSlots.map((slot) => (
            <BrandLogoSlot
              key={slot.kind}
              kind={slot.kind}
              logo={own[slot.kind]}
              inherited={companyLogos[slot.kind]}
              canManage
              onUpload={async (file) => {
                await businessUnitApi.uploadLogo(unit.id, slot.kind, file);
                await refreshBrand();
                snackbar.success(`${slot.title} da unidade enviada.`);
              }}
              onRemove={async () => {
                await businessUnitApi.removeLogo(unit.id, slot.kind);
                await refreshBrand();
                snackbar.success(`${slot.title}: a unidade volta a usar a da empresa.`);
              }}
            />
          ))}
        </div>
      </section>
      <div className="mt-6 border-t border-surface-border pt-6">
        <CancellationPolicySection target={{ kind: 'UNIT', id: unit.id }} level={3} />
      </div>
    </Modal>
  );
}
